import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { DchAutomobileLogo } from "../../assets/images";
import formatIcNo from "../../helpers/format.ic";

export const vehicleSalesOrderFileName =
  "DCH VSO FORD MUSTANG FASTBACK 2.3 (KFC37) -   RAGAVAN A L SIVAKUMAR.pdf";

const firstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const displayValue = (value, fallback = "-") =>
  value === undefined || value === null || value === ""
    ? fallback
    : String(value);

const amountValue = (value, fallback = "-") => {
  if (value === undefined || value === null || value === "") return fallback;

  const numericValue = Number(String(value).replace(/[^0-9.-]/g, ""));

  return Number.isFinite(numericValue)
    ? numericValue.toLocaleString("en-MY", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : String(value).replace(/^RM\s*/i, "");
};

const numericAmount = (value) => {
  const amount = Number(String(value ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(amount) ? amount : 0;
};

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return [
    String(date.getDate()).padStart(2, "0"),
    String(date.getMonth() + 1).padStart(2, "0"),
    date.getFullYear(),
  ].join(".");
};

export const vehicleSalesOrderSampleData = {
  date: "11.08.2026",
  customer: {
    name: "RAGAVAN A/L SIVAKUMAR",
    address: ["LOT 4 JALAN BUKIT BESAR", "09000 KULIM KEDAH"],
    identityNo: "930116-07-5639",
    contactNo: "60164212741",
  },
  vehicle: {
    registrationNo: "KFC37",
    makeModel: "FORD MUSTANG FASTBACK 2.3",
    year: "2019",
    chassisNo: "1FA6P8LH5K5110558",
    engineNo: "K5110558H",
  },
  sellingPrice: 170000,
  charges: {
    processingFee: 3000,
    roadTax: 641,
    insurance: 5766.6,
    ncd: "",
    othersOne: "",
    othersTwo: "",
    workshopAccessories: "",
  },
  payment: {
    firstDeposit: "",
    bank: "",
    hpRate: "",
    hiringPeriod: "",
    referenceNo: "",
    total: 0,
  },
};

export function createVehicleSalesOrderData(purchase = {}) {
  const order = purchase.source || purchase;
  const item = firstValue(order?.order_items?.[0], order?.items?.[0], {});
  const product = firstValue(
    order?.product,
    order?.reservation?.product,
    item?.product,
    item,
    {},
  );
  const identity = firstValue(
    order?.identity_details,
    order?.identity,
    order?.customer,
    order?.buyer,
    order?.registration,
    {},
  );
  const insurance = firstValue(
    order?.insurance_request,
    order?.insurance,
    order?.insurance_detail,
    {},
  );
  const properties = product?.properties || {};
  const customerName = firstValue(
    identity?.registration_type === "company" ? identity?.company_name : null,
    identity?.type === "company" ? identity?.company_name : null,
    identity?.company_name,
    identity?.name,
    identity?.full_name,
    [identity?.legal_first_name, identity?.legal_last_name]
      .filter(Boolean)
      .join(" "),
    order?.customer_name,
    order?.buyer_name,
  );
  const addressLineTwo = [identity?.postcode, identity?.city, identity?.state]
    .filter(Boolean)
    .join(" ");

  return {
    date: formatDate(firstValue(order?.sales_order_date, order?.created_at)),
    customer: {
      name: displayValue(customerName),
      address: [
        displayValue(firstValue(identity?.address, order?.customer_address)),
        addressLineTwo,
      ].filter(Boolean),
      identityNo: displayValue(
        identity?.registration_type === "company"
          ? identity?.company_register_no
          : formatIcNo(
              firstValue(identity?.ic_no, identity?.identity_no, order?.ic_no),
            ),
      ),
      contactNo: displayValue(
        firstValue(
          identity?.phone_number,
          order?.contact,
          order?.phone_number,
          order?.contact_number,
        ),
      ),
    },
    vehicle: {
      registrationNo: displayValue(
        firstValue(
          properties?.registration_no,
          properties?.car_plate,
          product?.car_plate,
        ),
      ),
      makeModel: displayValue(
        firstValue(
          "",
          [product?.brand?.name, product?.car_model?.name]
            .filter(Boolean)
            .join(" "),
          purchase?.name,
        ),
      ),
      year: displayValue(
        firstValue(product?.model_year, properties?.year, product?.year),
      ),
      chassisNo: displayValue(
        firstValue(properties?.chassis_no, product?.chassis_no),
      ),
      engineNo: displayValue(
        firstValue(properties?.engine_no, product?.engine_no),
      ),
    },
    sellingPrice: firstValue(
      order?.selling_price,
      order?.total_car_price,
      product?.final_price,
      product?.price,
      purchase?.sellingPrice,
      0,
    ),
    charges: {
      processingFee: firstValue(
        order?.handling_fees,
        order?.processing_fee,
        order?.handling_fee,
      ),
      // What the office set on the sale - never the estimate on the car page
      // (`road_tax`, `insurance_amount`). Blank until the office sets them.
      roadTax: firstValue(order?.one_year_road_tax),
      insurance: firstValue(order?.one_year_insurance),
      ncd: String(
        firstValue(insurance?.current_ncd, insurance?.ncd, "") || "",
      ).replace(/%/g, ""),
      othersOne: firstValue(order?.other_charge_one, ""),
      othersTwo: firstValue(order?.other_charge_two, ""),
      workshopAccessories: firstValue(
        order?.workshop_accessories,
        order?.accessories_amount,
        "",
      ),
    },
    payment: {
      firstDeposit: firstValue(
        order?.first_deposit,
        order?.deposit,
        order?.purpose === "reservation" &&
          ["paid", "success", "successful", "completed", "captured"].includes(
            order?.payment_status,
          )
          ? order?.total_price
          : undefined,
      ),
      bank: firstValue(order?.bank_name, order?.finance?.bank_name, ""),
      hpRate: firstValue(order?.hp_rate, order?.finance?.hp_rate, ""),
      hiringPeriod: firstValue(
        order?.hiring_period,
        order?.finance?.hiring_period,
        "",
      ),
      referenceNo: firstValue(
        order?.payment_reference,
        order?.transaction_number,
        order?.reference_no,
        "",
      ),
      total: firstValue(
        order?.total_collection,
        order?.total_payment,
        order?.paid_amount,
        0,
      ),
    },
  };
}

const styles = StyleSheet.create({
  page: {
    paddingTop: 50,
    paddingRight: 49.5,
    paddingBottom: 43,
    paddingLeft: 49.5,
    color: "#181515",
    backgroundColor: "#ffffff",
    fontFamily: "Helvetica",
    fontSize: 6.7,
  },
  headerTop: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  logo: {
    width: 197,
    height: 30.6,
    marginTop: 2.5,
    marginLeft: 10,
    objectFit: "contain",
  },
  company: { width: 257, paddingTop: 12.5 },
  companyName: {
    color: "#f00000",
    fontFamily: "Helvetica-Bold",
    fontSize: 6.8,
  },
  companyRegistration: { color: "#000000", fontSize: 3.7 },
  companyLine: { marginTop: 3, fontSize: 5.2 },
  companyContact: { marginTop: 1.5, fontSize: 5.2 },
  titleRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 15,
    paddingHorizontal: 16,
  },
  title: { fontFamily: "Helvetica-Bold", fontSize: 14.2 },
  date: { fontFamily: "Helvetica-Bold", fontSize: 8.5 },
  redRule: { height: 4.4, marginTop: 14.5, backgroundColor: "#f10d08" },
  customer: { marginTop: 11.5, marginBottom: 5, paddingHorizontal: 15 },
  sectionLabel: {
    color: "#1f3559",
    fontFamily: "Helvetica-Bold",
    fontSize: 7.2,
  },
  customerRows: { marginTop: 10 },
  detailRow: {
    display: "flex",
    flexDirection: "row",
    minHeight: 9.2,
    flexShrink: 0,
  },
  // Wrapped address lines sit right under the first one, with no colon of
  // their own, so they read as one address rather than a second field.
  // minHeight alone still leaves the row's own line box, so the line is pulled
  // up to sit flush under the first.
  detailRowContinued: { minHeight: 0, marginTop: -10 },
  detailLabel: { width: 105 },
  detailColon: { width: 13 },
  detailValue: { flex: 1, lineHeight: 1.15 },
  detailValueContinued: { lineHeight: 1 },
  table: {
    width: 469,
    marginTop: 3.5,
    marginHorizontal: 13.5,
  },
  tableRow: { display: "flex", flexDirection: "row", minHeight: 10 },
  tableHeader: {
    minHeight: 11,
    color: "#ffffff",
    backgroundColor: "#000000",
  },
  headerCell: {
    display: "flex",
    justifyContent: "center",
    paddingHorizontal: 2,
  },
  cell: {
    display: "flex",
    justifyContent: "center",
    paddingHorizontal: 2,
    borderRightWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: "#000000",
  },
  firstCell: { borderLeftWidth: 0.5 },
  topCell: { borderTopWidth: 0.5 },
  labelCell: { width: 104 },
  colonCell: { width: 11, alignItems: "center" },
  valueCell: { width: 251 },
  amountCell: { width: 103 },
  amountInner: { display: "flex", flexDirection: "row" },
  currency: { width: 27 },
  amount: { flex: 1, textAlign: "right", fontFamily: "Helvetica-Bold" },
  amountCurrency: { fontFamily: "Helvetica-Bold" },
  amountHighlight: {
    borderTopWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 1,
  },
  bold: { fontFamily: "Helvetica-Bold" },
  blue: { color: "#0000ee" },
  red: { color: "#ff0000" },
  spanningCell: { width: 366 },
  totalSellLabel: {
    width: 366,
    color: "#ffffff",
    backgroundColor: "#000000",
  },
  sectionUnderline: {
    alignSelf: "flex-start",
    paddingBottom: 0.15,
    borderBottomWidth: 0.5,
  },
  ncd: { textAlign: "right", paddingRight: 38 },
  balance: {
    width: 469,
    marginTop: 8.5,
    marginHorizontal: 13.5,
  },
  balanceCell: {
    borderTopWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
  },
  balanceFirstCell: { borderLeftWidth: 1 },
  financeValue: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  financePrimary: { width: 141 },
  financeSecondaryLabel: { width: 39, fontFamily: "Helvetica-Bold" },
  financeSecondaryValue: { flex: 1 },
  terms: { width: 469, marginTop: 11, marginHorizontal: 13.5 },
  termsTitle: { fontFamily: "Helvetica-Bold", fontSize: 7.2 },
  term: { marginTop: 2.3, fontSize: 5.25 },
  compulsory: { color: "#ff0000" },
  companyTerm: { fontFamily: "Helvetica-Bold" },
  bankDetails: { marginTop: 7, marginLeft: 16 },
  bankRow: { display: "flex", flexDirection: "row", minHeight: 8 },
  bankLabel: { width: 105 },
  bankValue: { fontFamily: "Helvetica-Bold" },
  signatureRule: { height: 1, marginTop: 5, backgroundColor: "#000000" },
  signatures: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 7,
    paddingHorizontal: 2,
  },
  signature: { width: 205 },
  signatureLabel: { fontFamily: "Helvetica-Bold" },
  signatureLine: { width: 104, marginTop: 29, borderTopWidth: 1 },
  signedSignatureLine: { marginTop: 0 },
  customerSignature: {
    width: 104,
    height: 29,
    objectFit: "contain",
    objectPositionX: "50%",
    objectPositionY: "100%",
  },
  signatureName: { marginTop: 4 },
  yellowRule: {
    height: 6.5,
    marginTop: 7,
    marginHorizontal: -14,
    backgroundColor: "#ffbf00",
  },
});

function DetailRow({ label, value, continued }) {
  return (
    <View
      style={
        continued
          ? [styles.detailRow, styles.detailRowContinued]
          : styles.detailRow
      }
    >
      <Text style={styles.detailLabel}>{label}</Text>
      <Text style={styles.detailColon}>{continued ? "" : ":"}</Text>
      <Text
        style={
          continued
            ? [styles.detailValue, styles.detailValueContinued]
            : styles.detailValue
        }
      >
        {value}
      </Text>
    </View>
  );
}

function AmountCell({ value, color, highlight, balance }) {
  return (
    <View
      style={[
        styles.cell,
        styles.amountCell,
        styles.amountInner,
        highlight && styles.amountHighlight,
        balance && styles.balanceCell,
      ]}
    >
      <Text style={[styles.currency, styles.amountCurrency, color]}>RM</Text>
      <Text style={[styles.amount, color]}>{amountValue(value)}</Text>
    </View>
  );
}

function TableRow({
  label = "",
  value = "",
  amount,
  bold,
  color,
  valueStyle,
  amountHighlight,
}) {
  return (
    <View style={styles.tableRow}>
      <View style={[styles.cell, styles.firstCell, styles.labelCell]}>
        <Text style={bold ? styles.bold : null}>{label}</Text>
      </View>
      <View style={[styles.cell, styles.colonCell]}>
        <Text>{label && label !== "Selling Price" ? ":" : ""}</Text>
      </View>
      <View style={[styles.cell, styles.valueCell]}>
        <Text style={[bold ? styles.bold : null, valueStyle]}>
          {displayValue(value, "")}
        </Text>
      </View>
      {amount === undefined ? (
        <View style={[styles.cell, styles.amountCell]} />
      ) : (
        <AmountCell value={amount} color={color} highlight={amountHighlight} />
      )}
    </View>
  );
}

function SectionRow({ title, amountTitle }) {
  return (
    <View style={[styles.tableRow, styles.tableHeader]}>
      <View style={[styles.headerCell, styles.spanningCell]}>
        <Text style={styles.bold}>{title}</Text>
      </View>
      <View
        style={[styles.headerCell, styles.amountCell, { alignItems: "center" }]}
      >
        <Text style={styles.bold}>{amountTitle || ""}</Text>
      </View>
    </View>
  );
}

function SpanningRow({ label, amount, color, black, highlight, top }) {
  return (
    <View style={styles.tableRow}>
      <View
        style={[
          styles.cell,
          styles.firstCell,
          styles.spanningCell,
          black && styles.totalSellLabel,
          top && styles.topCell,
        ]}
      >
        {label ? <Text style={styles.bold}>{label}</Text> : null}
      </View>
      {amount === undefined ? (
        <View style={[styles.cell, styles.amountCell, top && styles.topCell]} />
      ) : (
        <AmountCell value={amount} color={color} highlight={highlight} />
      )}
    </View>
  );
}

function TableSectionLabel({ children }) {
  return (
    <View style={styles.tableRow}>
      <View style={[styles.cell, styles.firstCell, styles.labelCell]}>
        <View style={styles.sectionUnderline}>
          <Text style={styles.bold}>{children}</Text>
        </View>
      </View>
      <View style={[styles.cell, styles.colonCell]} />
      <View style={[styles.cell, styles.valueCell]} />
      <View style={[styles.cell, styles.amountCell]} />
    </View>
  );
}

function BalanceRow({ amount }) {
  return (
    <View style={styles.tableRow}>
      <View
        style={[
          styles.cell,
          styles.spanningCell,
          styles.balanceCell,
          styles.balanceFirstCell,
        ]}
      >
        <Text style={styles.bold}>BALANCE TO PAID</Text>
      </View>
      <AmountCell value={amount} balance />
    </View>
  );
}

function FinanceRow({ label, value, suffix, secondaryLabel, secondaryValue }) {
  return (
    <View style={styles.tableRow}>
      <View style={[styles.cell, styles.firstCell, styles.labelCell]}>
        <Text>{label}</Text>
      </View>
      <View style={[styles.cell, styles.colonCell]}>
        <Text>:</Text>
      </View>
      <View style={[styles.cell, styles.valueCell, styles.financeValue]}>
        <Text style={styles.financePrimary}>
          {displayValue(value, "")}
          {suffix ? (
            <Text style={styles.bold}>
              {value ? " " : ""}
              {suffix}
            </Text>
          ) : null}
        </Text>
        <Text style={styles.financeSecondaryLabel}>{secondaryLabel} :</Text>
        <Text style={styles.financeSecondaryValue}>
          {displayValue(secondaryValue, "")}
        </Text>
      </View>
      <View style={[styles.cell, styles.amountCell]} />
    </View>
  );
}

export default function VehicleSalesOrderPdf({
  data = vehicleSalesOrderSampleData,
  fileName = vehicleSalesOrderFileName,
}) {
  const charges = data.charges || {};
  const payment = data.payment || {};
  const customer = data.customer || {};
  const vehicle = data.vehicle || {};
  const totalOtherCharges = firstValue(
    data.totalOtherCharges,
    numericAmount(charges.processingFee) +
      numericAmount(charges.roadTax) +
      numericAmount(charges.insurance) +
      numericAmount(charges.othersOne) +
      numericAmount(charges.othersTwo) +
      numericAmount(charges.workshopAccessories),
  );
  const totalSellPrice = firstValue(
    data.totalSellPrice,
    numericAmount(data.sellingPrice) + numericAmount(totalOtherCharges),
  );
  const totalPayment = numericAmount(payment.total);
  const balance = firstValue(
    data.balance,
    numericAmount(totalSellPrice) - totalPayment,
  );
  const address = Array.isArray(customer.address)
    ? customer.address
    : [customer.address];

  return (
    <Document
      title={fileName.replace(/\.pdf$/i, "")}
      author="DCH Automobile Sdn. Bhd."
    >
      <Page size="A4" style={styles.page}>
        <View style={styles.headerTop}>
          <Image src={DchAutomobileLogo} style={styles.logo} />
          <View style={styles.company}>
            <Text style={styles.companyName}>
              DCH AUTOMOBILE SDN. BHD.{" "}
              <Text style={styles.companyRegistration}>
                202301046619 (1540533-U)
              </Text>
            </Text>
            <Text style={styles.companyLine}>
              NO. 7, JALAN ASTANA 1F/KU2, BANDAR BUKIT RAJA, 41050 KLANG,
              SELANGOR , MALAYSIA.
            </Text>
            <Text style={styles.companyContact}>
              Tel : 03-7800 9099 Email : admin_dchautomobile@dhub.my
            </Text>
          </View>
        </View>

        <View style={styles.titleRow}>
          <Text style={styles.title}>VEHICLE SALES ORDER</Text>
          <Text style={styles.date}>DATE : {displayValue(data.date)}</Text>
        </View>
        <View style={styles.redRule} />

        <View style={styles.customer} wrap={false}>
          <View style={styles.sectionUnderline}>
            <Text style={styles.sectionLabel}>CUSTOMER DETAILS :</Text>
          </View>
          <View style={styles.customerRows}>
            <DetailRow label="Name" value={displayValue(customer.name)} />
            <DetailRow label="Address" value={displayValue(address[0])} />
            {address.slice(1).map((line, index) => (
              <DetailRow
                label=""
                value={displayValue(line, "")}
                key={index}
                continued
              />
            ))}
            <DetailRow
              label="IC No."
              value={displayValue(customer.identityNo)}
            />
            <DetailRow
              label="Contact No."
              value={displayValue(customer.contactNo)}
            />
          </View>
        </View>

        <View style={styles.table}>
          <SectionRow title="VEHICLE DESCRIPTION" amountTitle="AMOUNT" />
          <SpanningRow top />
          <TableSectionLabel>VEHICLE DESCRIPTION :</TableSectionLabel>
          <TableRow label="Registration No." value={vehicle.registrationNo} />
          <TableRow label="Make & Model" value={vehicle.makeModel} />
          <TableRow label="Year of Manufactured" value={vehicle.year} />
          <TableRow label="Chassis No." value={vehicle.chassisNo} />
          <TableRow label="Engine No." value={vehicle.engineNo} />
          <TableRow />
          <TableRow
            label="Selling Price"
            amount={data.sellingPrice}
            bold
            color={styles.blue}
            amountHighlight
          />
          <TableRow />
          <TableSectionLabel>OTHERS CHARGES :</TableSectionLabel>
          <TableRow
            label="Processing & Handling Fee"
            amount={charges.processingFee}
          />
          <TableRow label="One Year Road Tax" amount={charges.roadTax} />
          <TableRow label="One Year Insurance" amount={charges.insurance} />
          <TableRow label="Others" value={charges.othersOne} />
          <TableRow label="Others" value={charges.othersTwo} />
          <TableRow
            label="Workshop / Accessories"
            value={charges.workshopAccessories}
          />
          <TableRow />
          <TableRow
            label="TOTAL OTHER CHARGES"
            amount={totalOtherCharges}
            bold
            color={styles.blue}
            amountHighlight
          />
          <SpanningRow />
          <SpanningRow
            label="TOTAL SELL PRICE"
            amount={totalSellPrice}
            color={styles.red}
            black
            highlight
          />
          <SpanningRow />
          <TableSectionLabel>PAYMENT :</TableSectionLabel>
          <TableRow label="1st Deposit" amount={payment.firstDeposit} />
          <TableRow />
          <TableRow />
          <TableRow />
          <FinanceRow
            label="Bank"
            value={payment.bank}
            secondaryLabel="HP Rate"
            secondaryValue={payment.hpRate}
          />
          <FinanceRow
            label="Hiring Period"
            value={payment.hiringPeriod}
            suffix="months"
            secondaryLabel="Ref No"
            secondaryValue={payment.referenceNo}
          />
          <TableRow
            label="TOTAL PAYMENT"
            amount={totalPayment || "-"}
            amountHighlight
          />
        </View>

        <View style={styles.balance}>
          <BalanceRow amount={balance} />
        </View>

        <View style={styles.terms}>
          <View style={styles.sectionUnderline}>
            <Text style={styles.termsTitle}>TERMS AND CONDITIONS :</Text>
          </View>
          <Text style={styles.term}>
            1) The Seller agrees to sell and the Buyer agrees to buy the vehicle
            described above for the Total Sale Price listed.
          </Text>
          <Text style={styles.term}>
            2) The vehicle is sold &quot;as is&quot; without any warranties or
            guarantees.
          </Text>
          <Text style={styles.term}>
            3) The title and ownership of the vehicle will be transferred to the
            Buyer upon full payment.
          </Text>
          <Text style={styles.term}>
            4) Please note that the FIRST YEAR CAR INSURANCE is{" "}
            <Text style={styles.compulsory}>compulsory</Text> to be issued by
            our company.
          </Text>
          <Text style={styles.term}>
            5) All payment must be made payable to{" "}
            <Text style={styles.companyTerm}>DCH AUTOMOBILE SDN BHD.</Text>
          </Text>
          <Text style={styles.term}>
            6) The bank details of the Company as follows :
          </Text>
        </View>

        <View style={styles.bankDetails}>
          <View style={styles.bankRow}>
            <Text style={styles.bankLabel}>Company Name</Text>
            <Text style={styles.bankValue}>: DCH AUTOMOBILE SDN BHD</Text>
          </View>
          <View style={styles.bankRow}>
            <Text style={styles.bankLabel}>Bank Name</Text>
            <Text style={styles.bankValue}>: STANDARD CHARTERED</Text>
          </View>
          <View style={styles.bankRow}>
            <Text style={styles.bankLabel}>Account No.</Text>
            <Text style={styles.bankValue}>: 708409939989</Text>
          </View>
        </View>

        <View style={styles.signatureRule} />
        <View style={styles.signatures}>
          <View style={styles.signature}>
            <Text style={styles.signatureLabel}>
              Signed by DCH Automobile Sdn Bhd
            </Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureName}>Authorised Signature</Text>
          </View>
          <View style={styles.signature}>
            <Text style={styles.signatureLabel}>Signed by Customer</Text>
            {data.customerSignature && (
              <Image
                src={data.customerSignature}
                style={styles.customerSignature}
              />
            )}
            <View
              style={[
                styles.signatureLine,
                data.customerSignature && styles.signedSignatureLine,
              ]}
            />
            <Text style={styles.signatureName}>
              Name : {displayValue(customer.name)}
            </Text>
          </View>
        </View>
        <View style={styles.yellowRule} />
      </Page>
    </Document>
  );
}
